import { useCallback, useEffect, useState } from "react";

import { getErrorMessage, isAPIError } from "@/core/services";
import { HISTORICO_ERROR_MESSAGE, HISTORICO_FORBIDDEN_MESSAGE } from "./simulacao.textos";
import { listarHistoricoSimulacoes } from "../simulacao.gateway";
import type { SimulacaoResumoApi, StatusHistoricoSimulacaoApi } from "../simulacao.gateway";

export type HistoricoQueryStatus = StatusHistoricoSimulacaoApi | null;

type HistoricoStatus = "loading" | "success" | "error" | "forbidden";

interface HistoricoQueryState {
  status: HistoricoStatus;
  itens: SimulacaoResumoApi[];
  total: number;
  errorMessage: string | null;
}

const INITIAL_STATE: HistoricoQueryState = {
  status: "loading",
  itens: [],
  total: 0,
  errorMessage: null,
};

export interface UseHistoricoSimulacoesParams {
  page: number;
  pageSize: number;
  status: HistoricoQueryStatus;
  enabled?: boolean;
}

interface UseHistoricoSimulacoesResult extends HistoricoQueryState {
  reload: () => void;
}

export function useHistoricoSimulacoes({
  page,
  pageSize,
  status,
  enabled = true,
}: UseHistoricoSimulacoesParams): UseHistoricoSimulacoesResult {
  const [state, setState] = useState<HistoricoQueryState>(INITIAL_STATE);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    const controller = new AbortController();

    setState((previous) => ({ ...previous, status: "loading", errorMessage: null }));

    listarHistoricoSimulacoes(
      {
        page,
        pageSize,
        ...(typeof status === "string" && status.length > 0 ? { status } : {}),
      },
      controller.signal,
    )
      .then(({ itens, total }) => {
        if (cancelled) return;
        setState({ status: "success", itens, total, errorMessage: null });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const isForbidden = isAPIError(error) && error.status === 403;
        setState({
          status: isForbidden ? "forbidden" : "error",
          itens: [],
          total: 0,
          errorMessage: isForbidden
            ? HISTORICO_FORBIDDEN_MESSAGE
            : getErrorMessage(error) || HISTORICO_ERROR_MESSAGE,
        });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [page, pageSize, status, enabled, reloadToken]);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  return { ...state, reload };
}
